import { useNavigate } from "react-router-dom";
import { MapPin, Heart, BadgeCheck, Columns2, Zap, Tag, Star } from "lucide-react";
import { toast } from "sonner";
import { imageUrl } from "@/lib/api";
import { primeCache } from "@/lib/cache";
import { cityName, formatPrice, servicePriceText, discountPct, adText, cardPriceNote } from "@/lib/constants";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";

// Labor centers + all home services: price only on the ad detail page, not on cards.
const HIDE_CARD_PRICE = new Set(["domestic_workers", "home_services"]);
const NEW_MS = 2 * 24 * 60 * 60 * 1000;

export function ListingCard({ listing, big = false }) {
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const { user, toggleFavorite } = useAuth();
  const cover = listing.images?.[0];
  const isFav = user?.favorites?.includes(listing.id);
  const hidePrice = HIDE_CARD_PRICE.has(listing.category);
  const pct = discountPct(listing) || (hidePrice ? Math.round(listing.discount_percent || 0) : 0);
  const featured = listing.featured && (!listing.featured_until || new Date(listing.featured_until) > new Date());
  const publishedAt = listing.approved_at || listing.created_at;
  const isNew = publishedAt && Date.now() - new Date(publishedAt).getTime() < NEW_MS;
  const offTxt = lang === "en" ? `${pct}% ${t("discount_off")}` : `${t("discount_off")} \u2066${pct}%\u2069`;

  const onFav = async (e) => {
    e.stopPropagation();
    if (!user) { navigate("/login"); return; }
    try { await toggleFavorite(listing.id); } catch { toast.error(lang === "en" ? "Couldn't update favourites" : "تعذّر تحديث المفضلة"); }
  };

  return (
    <div
      onClick={() => { primeCache(`/listings/${listing.id}`, listing); navigate(`/listing/${listing.id}`); }}
      data-testid={`listing-card-${listing.id}`}
      className="group h-full flex flex-col bg-white rounded-3xl border border-[#EAE6DB] overflow-hidden cursor-pointer hover:shadow-lg transition-shadow duration-300"
    >
      <div className="relative aspect-square overflow-hidden bg-slate-100">
        {cover ? (
          <img src={imageUrl(cover)} alt={adText(listing, "title", lang)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full bg-slate-100" />
        )}
        <button onClick={onFav} data-testid={`favorite-button-${listing.id}`} className="absolute bottom-3 end-3 bg-white rounded-full p-2 shadow-sm hover:scale-110 transition-transform">
          <Heart className={`w-4 h-4 ${isFav ? "fill-red-500 text-red-500" : "text-slate-500"}`} />
        </button>
        {pct > 0 && (
          <span className="absolute top-3 end-3 flex items-center gap-1 bg-red-600 text-white text-[11px] font-bold px-2 py-1 rounded-full shadow-sm" data-testid={`discount-badge-${listing.id}`}><Tag className="w-3.5 h-3.5" />{offTxt}</span>
        )}
        <span className="absolute bottom-3 start-3 bg-white/95 rounded-lg p-1.5 shadow-sm">
          <Columns2 className="w-4 h-4 text-[#1A5C3A]" />
        </span>
      </div>
      <div className="p-3.5 flex-1 flex flex-col">
        {(isNew || featured) && (
          <div className="flex items-center gap-1.5 mb-2" data-testid={`card-badges-${listing.id}`}>
            {isNew && <span className="flex items-center gap-1 bg-red-600 text-white text-[11px] font-bold px-2 py-0.5 rounded-full" data-testid={`new-ad-badge-${listing.id}`}><Zap className="w-3.5 h-3.5 fill-current" />{lang === "en" ? "New" : "جديد"}</span>}
            {featured && <span className="flex items-center gap-1 bg-[#D4AF37] text-white text-[11px] font-bold px-2 py-0.5 rounded-full" data-testid={`featured-badge-${listing.id}`}><Star className="w-3.5 h-3.5 fill-current" />{lang === "en" ? "Featured" : "مميز"}</span>}
          </div>
        )}
        <h3 className={`font-headline font-semibold text-slate-900 line-clamp-1 mb-2 ${big ? "text-base" : "text-sm"}`} data-testid={`listing-card-title-${listing.id}`}>{adText(listing, "title", lang)}</h3>
        {hidePrice ? (
          <p className="text-sm font-bold text-[#1A5C3A] mb-1.5 line-clamp-1" data-testid={`card-price-note-${listing.id}`}>{cardPriceNote(listing, lang)}</p>
        ) : (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1.5">
          <span className={`text-[#1A5C3A] font-headline font-extrabold whitespace-nowrap ${big ? "text-xl" : "text-lg"}`} dir={servicePriceText(listing, lang) ? "auto" : "ltr"} data-testid={`card-price-${listing.id}`}>{servicePriceText(listing, lang) || formatPrice(listing.price, lang)}</span>
          {pct > 0 && listing.original_price > 0 && <>
            <span className="bg-red-50 text-red-600 text-[11px] font-bold px-1.5 py-0.5 rounded-md whitespace-nowrap" data-testid={`price-discount-badge-${listing.id}`}>{offTxt}</span>
            <span className="text-xs text-slate-400 line-through whitespace-nowrap" dir="ltr" data-testid={`original-price-${listing.id}`}>{formatPrice(listing.original_price, lang)}</span>
          </>}
        </div>
        )}
        <div className="flex items-center justify-between gap-2 mt-auto">
          <span className={`flex items-center gap-1 min-w-0 ${big ? "text-sm" : "text-xs"} text-slate-500`}>
            <MapPin className="w-3.5 h-3.5 shrink-0" /> <span className="truncate">{cityName(listing.city, lang)}</span>
          </span>
          <span className="flex flex-wrap justify-end items-center gap-1">
            {listing.seller_verified && <span className="flex items-center gap-1 bg-[#EAF5F0] text-[#1A5C3A] text-[11px] font-semibold px-2 py-0.5 rounded-full" data-testid={`verified-badge-${listing.id}`}>
              <BadgeCheck className="w-3.5 h-3.5" /> {t("verified")}
            </span>}
            {listing.condition === "new" && <span className="bg-[#FFF4D6] text-[#9A6B12] text-[11px] font-bold px-2 py-0.5 rounded-full" data-testid={`new-badge-${listing.id}`}>{lang === "en" ? "New" : "جديد"}</span>}
          </span>
        </div>
      </div>
    </div>
  );
}
